import * as z from 'zod';

import * as api from '@gitbook/api-client';
import { MemberRole, type MemberRoleOrGuest } from '@gitbook/api-client';
import type { OrderIndexPosition } from '@gitbook/util-orderindex';
import { arrayOfAll } from '@gitbook/util-ts';

/*
 * Common settings applying to both spaces and contents.
 */
export const ContentOwnershipSchema = z.object({
    organization: z.string(),
    collection: z.string().optional(),
});
export type ContentOwnership = z.infer<typeof ContentOwnershipSchema>;

export type ContentPointer = api.SpacePointer | api.CollectionPointer;

/** Ranks and content IDs needed to repair duplicate sibling ranks. */
export type ContentPositionResult = OrderIndexPosition & {
    afterContent?: ContentPointer;
    afterOwner?: ContentOwnership;
};

export const ALL_CONTENT_FONTS = arrayOfAll<api.CustomizationDefaultFont>()([
    api.CustomizationDefaultFont.ABCFavorit,
    api.CustomizationDefaultFont.Inter,
    api.CustomizationDefaultFont.Roboto,
    api.CustomizationDefaultFont.RobotoSlab,
    api.CustomizationDefaultFont.OpenSans,
    api.CustomizationDefaultFont.SourceSansPro,
    api.CustomizationDefaultFont.Lato,
    api.CustomizationDefaultFont.Ubuntu,
    api.CustomizationDefaultFont.Raleway,
    api.CustomizationDefaultFont.Merriweather,
    api.CustomizationDefaultFont.Overpass,
    api.CustomizationDefaultFont.NotoSans,
    api.CustomizationDefaultFont.IBMPlexSerif,
    api.CustomizationDefaultFont.Poppins,
    api.CustomizationDefaultFont.FiraSans,
]);

export const ALL_MONOSPACE_FONTS = arrayOfAll<api.CustomizationDefaultMonospaceFont>()([
    api.CustomizationDefaultMonospaceFont.IBMPlexMono,
    api.CustomizationDefaultMonospaceFont.FiraCode,
    api.CustomizationDefaultMonospaceFont.JetBrainsMono,
    api.CustomizationDefaultMonospaceFont.SourceCodePro,
    api.CustomizationDefaultMonospaceFont.RobotoMono,
    api.CustomizationDefaultMonospaceFont.SpaceMono,
    api.CustomizationDefaultMonospaceFont.DMMono,
    api.CustomizationDefaultMonospaceFont.Inconsolata,
]);

export const THEME_PRESETS: Array<{
    theme: api.CustomizationHeaderPreset;
    label: string;
}> = [
    {
        theme: api.CustomizationHeaderPreset.Default,
        label: 'Default',
    },
    {
        theme: api.CustomizationHeaderPreset.Bold,
        label: 'Bold',
    },
    {
        theme: api.CustomizationHeaderPreset.Contrast,
        label: 'Contrast',
    },
    {
        theme: api.CustomizationHeaderPreset.Custom,
        label: 'Custom',
    },
];

/**
 * Pre-set theme for the content. Comes with a set of colors and fonts.
 */
export const ContentThemePresetSchema = z.enum(['default', 'bold', 'contrast', 'custom']);
export type ContentThemePreset = z.infer<typeof ContentThemePresetSchema>;

/**
 * Theme mode - light, dark (based on browser settings).
 */
const ContentThemeModeSchema = z.enum(api.CustomizationThemeMode);
export type ContentThemeMode = z.infer<typeof ContentThemeModeSchema>;

/**
 * Theme corner.
 */
export const ContentThemeCornersSchema = z.enum(['straight', 'rounded']);
export type ContentThemeCorners = z.infer<typeof ContentThemeCornersSchema>;

/**
 * Theme background.
 */
export const ContentThemeBackgroundSchema = z.enum(['plain', 'match']);
export type ContentThemeBackground = z.infer<typeof ContentThemeBackgroundSchema>;

/**
 * Deafult theme mode for content, light mode.
 */
export const THEME_MODE_DEFAULT = api.CustomizationThemeMode.Light;

/**
 * Deafult theme corners for content, rounded.
 */
export const THEME_CORNER_DEFAULT = 'rounded';

const ContentThemeSchema = z.object({
    color: ContentThemeModeSchema,
    radius: ContentThemeCornersSchema,
});
export type ContentTheme = z.infer<typeof ContentThemeSchema>;

/**
 * Level of access for a team or user.
 */
export { MemberRole as ContentPermissionLevel };
export const ContentPermissionLevelOrNoneSchema = z.union([z.enum(MemberRole), z.null()]);
export type ContentPermissionLevelOrNone = MemberRoleOrGuest;

/**
 * Default permission for a piece of content.
 */
const DefaultPermissionLevelSchema = z.union([
    ContentPermissionLevelOrNoneSchema,
    z.literal('inherit'),
]);
export type DefaultPermissionLevel = z.infer<typeof DefaultPermissionLevelSchema>;

export const URLSchema = z.url().max(2048);

/**
 * Configuration for sharing collections or spaces.
 */
export const ContentSharingSchema = ContentOwnershipSchema.extend({
    /**
     * Permission for people who can access the parent (org or collection).
     * null means that the content cannot be accessed
     */
    defaultPermission: DefaultPermissionLevelSchema,
});
export type ContentSharing = z.infer<typeof ContentSharingSchema>;

/**
 * List of all valid ContentPermissionLevel.
 *
 * The list must be kept in order of importance i.e. the first entry must have all the permissions of the entries below it.
 */
export const ALL_CONTENT_PERMISSIONS: ContentPermissionLevelOrNone[] = [
    MemberRole.Admin,
    MemberRole.Create,
    MemberRole.Review,
    MemberRole.Edit,
    MemberRole.Comment,
    MemberRole.Read,
    null,
];

const ContentPermissionsSchema = z.object({
    level: ContentPermissionLevelOrNoneSchema,
    /**
     * The user who created this permission.
     */
    createdBy: z.string(),
    /**
     * The user who deleted this permission, if it's deleted or being deleted.
     */
    deletedBy: z.string().optional(),
});

/**
 * Permissions for a team in a content.
 */
export const TeamContentPermissionsSchema = ContentPermissionsSchema.extend({
    organization: z.string(),
    team: z.string(),
});
export type TeamContentPermissions = z.infer<typeof TeamContentPermissionsSchema>;

/**
 * Permissions for a user in a content.
 */
export const UserContentPermissionsSchema = ContentPermissionsSchema.extend({
    user: z.string(),
    /**
     * The invite link used to grant this user access, if needed.
     */
    invite: z.string().optional(),
});
export type UserContentPermissions = z.infer<typeof UserContentPermissionsSchema>;

/**
 * Compare two ownerships. Return true if equals.
 */
export function compareContentOwners(
    a: ContentOwnership,
    b: ContentOwnership,
    shouldCompareCollections = false
): boolean {
    if (a.organization !== b.organization) {
        return false;
    }

    if (shouldCompareCollections && a.collection !== b.collection) {
        return false;
    }

    return true;
}
